import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import KpiCard from '../components/KpiCard';
import ErrorState from '../components/ErrorState';
import { KPI_CONFIGS } from '../config/kpis';
import { HiHome, HiArrowPath, HiChartBar, HiSignal, HiUserGroup } from 'react-icons/hi2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';

const safeQuery = async (fn, fallback = 0) => {
  try {
    return await fn();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('DevOverview query failed:', err?.message);
    return fallback;
  }
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

const DevOverviewPage = () => {
  const [kpis, setKpis] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [kpiChartData, setKpiChartData] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverviewData = useCallback(async () => {
    setRefreshing(true);
    setError(null);

    try {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const kpiValues = {};

      // DAU
      kpiValues.dau = await safeQuery(async () => {
        const q = query(
          collection(firestore, 'userActivity'),
          where('createdAt', '>=', Timestamp.fromDate(oneDayAgo))
        );
        const snap = await getDocs(q);
        const unique = new Set(snap.docs.map(d => d.data().userId).filter(Boolean));
        return unique.size;
      });

      // WAU
      kpiValues.wau = await safeQuery(async () => {
        const q = query(
          collection(firestore, 'userActivity'),
          where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo))
        );
        const snap = await getDocs(q);
        const unique = new Set(snap.docs.map(d => d.data().userId).filter(Boolean));
        return unique.size;
      });

      // Active Sessions
      kpiValues.activeSessions = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'sessions'));
        return snap.docs.filter(d => {
          const s = d.data();
          return s.status === 'scheduled' || s.status === 'active' || s.active === true;
        }).length;
      });

      // OTP Rate
      kpiValues.otpRate = await safeQuery(async () => {
        let total = 0;
        const otpsSnap = await getDocs(
          query(
            collection(firestore, 'otps'),
            where('createdAt', '>=', Timestamp.fromDate(oneDayAgo))
          )
        );
        total += otpsSnap?.size ?? 0;
        try {
          const clientSnap = await getDocs(
            query(
              collection(firestore, 'clientLoginOtps'),
              where('createdAt', '>=', Timestamp.fromDate(oneDayAgo))
            )
          );
          total += clientSnap?.size ?? 0;
        } catch (_) {}
        try {
          const resetSnap = await getDocs(
            query(
              collection(firestore, 'passwordResetOtps'),
              where('createdAt', '>=', Timestamp.fromDate(oneDayAgo))
            )
          );
          total += resetSnap?.size ?? 0;
        } catch (_) {}
        return total;
      });

      // Document Views
      kpiValues.docViews = await safeQuery(async () => {
        const q = query(
          collection(firestore, 'documentBankViews'),
          where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo))
        );
        const snap = await getDocs(q);
        return snap.size;
      });

      // Security Incidents (security_logs uses timestamp)
      kpiValues.securityIncidents = await safeQuery(async () => {
        try {
          const q = query(
            collection(firestore, 'security_logs'),
            where('timestamp', '>=', Timestamp.fromDate(sevenDaysAgo))
          );
          const snap = await getDocs(q);
          return snap.docs.filter(d =>
            ['high', 'critical'].includes((d.data().severity || '').toLowerCase())
          ).length;
        } catch {
          const q = query(
            collection(firestore, 'security_logs'),
            where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo))
          );
          const snap = await getDocs(q);
          return snap.docs.filter(d =>
            ['high', 'critical'].includes((d.data().severity || '').toLowerCase())
          ).length;
        }
      });

      // Knowledge Base Usage
      kpiValues.kbUsage = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'knowledgeBaseAnalytics'));
        return snap.size;
      });

      // Template Usage
      kpiValues.templateUsage = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'templatesAnalytics'));
        return snap.size;
      });

      // Program Ratings
      kpiValues.programRatings = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'programRatings'));
        if (snap.empty) return null;
        const ratings = snap.docs.map(d => d.data().rating).filter(r => typeof r === 'number');
        return ratings.length > 0
          ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
          : null;
      }, null);

      // Coaching Engagement
      kpiValues.coachingEngagement = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'sessions'));
        return snap.size;
      });

      // Product Dev Throughput
      kpiValues.productThroughput = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'productDevelopmentSubmissions'));
        return snap.size;
      });

      // Audit Pass Rate
      kpiValues.auditPassRate = await safeQuery(async () => {
        const snap = await getDocs(collection(firestore, 'productAudits'));
        if (snap.empty) return null;
        const passed = snap.docs.filter(
          d => d.data().passed === true || d.data().status === 'passed'
        ).length;
        return Math.round((passed / snap.size) * 100);
      }, null);

      // Org & Client Growth
      kpiValues.orgClientGrowth = await safeQuery(async () => {
        const [orgs, clients] = await Promise.all([
          getDocs(collection(firestore, 'organizations')),
          getDocs(collection(firestore, 'clients')),
        ]);
        return (orgs?.size || 0) + (clients?.size || 0);
      });

      setKpis(kpiValues);

      // Trend data: daily active users for last 7 days (single query + client-side bucket)
      const trend = await safeQuery(async () => {
        const q = query(
          collection(firestore, 'userActivity'),
          where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo))
        );
        const snap = await getDocs(q);
        const byDay = {};
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          d.setHours(0, 0, 0, 0);
          byDay[d.toDateString()] = new Set();
        }
        snap.docs.forEach(doc => {
          const data = doc.data();
          const ts = data.createdAt?.toMillis?.() ? new Date(data.createdAt.toMillis()) : null;
          if (ts && data.userId) {
            const key = new Date(ts.getFullYear(), ts.getMonth(), ts.getDate()).toDateString();
            if (byDay[key]) byDay[key].add(data.userId);
          }
        });
        return Object.entries(byDay)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .map(([dateStr, users]) => {
            const d = new Date(dateStr);
            return {
              day: d.toLocaleDateString('en-US', { weekday: 'short' }),
              users: users.size,
              date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            };
          });
      }, []);
      setTrendData(Array.isArray(trend) ? trend : []);

      // KPI chart data (top metrics for pie/bar)
      const chartData = [
        { name: 'DAU', value: kpiValues.dau || 0 },
        { name: 'Sessions', value: kpiValues.activeSessions || 0 },
        { name: 'OTPs', value: kpiValues.otpRate || 0 },
        { name: 'Doc Views', value: kpiValues.docViews || 0 },
        { name: 'KB Usage', value: kpiValues.kbUsage || 0 },
        { name: 'Templates', value: kpiValues.templateUsage || 0 },
      ].filter(d => d.value > 0);
      setKpiChartData(chartData);

      setLastUpdated(new Date());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading overview data:', err);
      setError(err?.message || 'Failed to load overview');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadOverviewData();
  }, [loadOverviewData]);

  if (error) {
    return <ErrorState message={error} onRetry={loadOverviewData} />;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 shadow-xl">
        <p className="text-xs font-medium text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-white">{payload[0].value} users</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/25">
            <HiHome className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Overview</h1>
            <p className="text-sm text-slate-400">
              Key metrics, trends, and activity across the platform
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadOverviewData}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-blue-500/50 hover:bg-slate-700 disabled:opacity-60"
          >
            <HiArrowPath className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </motion.button>
        </div>
      </motion.header>

      {/* KPI Cards */}
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <div className="mb-4 flex items-center gap-2">
          <HiChartBar className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Key Performance Indicators</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {KPI_CONFIGS.map((kpi, index) => (
              <motion.div
                key={kpi.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="h-full"
              >
                <KpiCard
                  kpi={kpi}
                  value={kpis[kpi.id] !== undefined ? kpis[kpi.id] : 0}
                  isLoading={false}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.section>

      {/* Charts Row */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        {/* Activity Trend */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
          <div className="mb-4 flex items-center gap-2">
            <HiSignal className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Daily Active Users (7 days)</h2>
          </div>
          <div className="h-64">
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                No trend data yet
              </div>
            )}
          </div>
        </div>

        {/* KPI Distribution */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
          <div className="mb-4 flex items-center gap-2">
            <HiUserGroup className="h-5 w-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Metric Distribution</h2>
          </div>
          <div className="h-64">
            {kpiChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpiChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={12} width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                No metrics to display
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Pie Chart - Engagement breakdown */}
      {kpiChartData.length > 0 && (
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <HiChartBar className="h-5 w-5 text-violet-400" />
            <h2 className="text-lg font-semibold text-white">Engagement Overview</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={kpiChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {kpiChartData.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.section>
      )}
    </div>
  );
};

export default DevOverviewPage;
